import { useRef } from "react"
import { Alert, Platform, TextStyle, View, ViewStyle } from "react-native"

import { observer } from "mobx-react-lite"
import { TMessage } from "types/transcript"

import { useChat } from "src/context/ChatContext"
import { useTyping } from "src/context/TypingContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"
import { nowInUTC } from "src/utils/time"
import { TextField } from "../Ignite"
import { SendButton } from "./SendButton"

const CHAT_MAX_CHARS = 5000

export const ChatTextInput = observer((props: { onSend?: (message: TMessage) => void }) => {
  const { themed } = useAppTheme()
  const { isSwitchingSidekick, wordsDone, isSidekickBusy } = useChat()
  const { draftMessage, setDraftMessage } = useTyping()
  const limitAlertShown = useRef(false)

  const handleChangeText = (text: string) => {
    if (text.length > CHAT_MAX_CHARS) {
      setDraftMessage(text.slice(0, CHAT_MAX_CHARS))
      if (!limitAlertShown.current) {
        limitAlertShown.current = true
        Alert.alert(
          "Character Limit Reached",
          "You have reached the maximum number of characters you can enter into the chat",
          [
            {
              text: "Okay",
              onPress: () => {
                limitAlertShown.current = false
              },
            },
          ],
        )
      }
      return
    }
    limitAlertShown.current = false
    setDraftMessage(text)
  }

  const sendMessage = () => {
    if (draftMessage && draftMessage.trim().length > 0) {
      props.onSend?.({
        text: draftMessage,
        role: "user",
        created_at: nowInUTC(),
        id: new Date().getTime(),
      })
      setDraftMessage("")
    }
  }

  // WIP: Merging with MR !596.  We think having wordsDone is important to prevent sending messages while the sidekick is generating a response.
  const isDisabled = isSwitchingSidekick || !wordsDone || isSidekickBusy

  return (
    <View style={themed($container)}>
      <TextField
        style={themed($input)}
        containerStyle={$flex}
        inputWrapperStyle={themed($inputWrapper)}
        value={draftMessage}
        autoFocus={true}
        // editable={!props.sttEnabled}
        placeholder={"Type a message"}
        onChangeText={handleChangeText}
        returnKeyType="send"
        onSubmitEditing={() => {
          if (!!draftMessage && !isDisabled) {
            sendMessage()
          }
        }}
        // On web, multiline TextInput renders as a <textarea> where Enter inserts a newline
        // instead of submitting. Intercept Enter (without Shift) to send; Shift+Enter adds a newline.
        onKeyPress={
          Platform.OS === "web"
            ? (e: any) => {
                if (e.nativeEvent.key === "Enter" && !e.nativeEvent.shiftKey) {
                  e.preventDefault()
                  if (draftMessage && !isDisabled) {
                    sendMessage()
                  }
                }
              }
            : undefined
        }
        status={isDisabled ? "disabled" : undefined}
        // Enable multiline and scrolling for long messages
        multiline={true}
        scrollEnabled={true}
        textAlignVertical="top"
        blurOnSubmit={false} // Keep keyboard open after sending
        submitBehavior="submit"
        // RightAccessory={() =>
        //   props.sttEnabled &&
        //   props.sttListening && (
        //     <WaveformAnimation
        //       volume={props.volume || 0}
        //       barCount={9} // We want an odd number of bars so there's a center bar.
        //       barWidth={5}
        //       height={40}
        //     />
        //   )
        // }
      />
      <SendButton onPress={sendMessage} canSend={!!draftMessage && !isDisabled} />
    </View>
  )
})

const $flex: ViewStyle = {
  flex: 1,
}

const $container: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  flexDirection: "row",
  padding: spacing.xxs,
  paddingHorizontal: spacing.sm,
  alignItems: "center",
  backgroundColor: colors.background,
})

const $input: ThemedStyle<TextStyle> = ({ colors }) => ({
  backgroundColor: colors.background,
  color: colors.inputText,
})

const $inputWrapper: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.background,
  borderColor: colors.cardBorder,
  borderWidth: 1,
  minHeight: 40,
  maxHeight: 120, // Limit height to ~5 lines of text
  paddingVertical: 8,
  borderRadius: 20,
})
